import { useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/firebase/app";
import * as coreApi from "@/utils/api/core";
import type { RapidWorkflowDataType } from "@/components/flow/shared/Deploy/documentation/types";
import type { CreateRapidWorkflowProps } from "@/types/workflow";

export const workflowKeys = {
    all: ["workflows"] as const,
    byUrl: (url: string, workspaceId: string) =>
        [...workflowKeys.all, "byUrl", url, workspaceId] as const,
    bySlug: (slug: string) => [...workflowKeys.all, "bySlug", slug] as const,
    byId: (id: string) => [...workflowKeys.all, "byId", id] as const
};

export function useWorkflowByUrl(
    url: string | undefined,
    workspaceId: string | undefined,
    activeSession: boolean
) {
    return useQuery<RapidWorkflowDataType>({
        queryKey: workflowKeys.byUrl(url!, workspaceId!),
        queryFn: () => coreApi.getRapidWorkflow(url!, workspaceId!),
        enabled: !!url && !!workspaceId && activeSession,
        staleTime: Infinity
    });
}

export function usePublicWorkflowBySlug(slug: string | undefined, guestAuthInitialized: boolean) {
    return useQuery<RapidWorkflowDataType>({
        queryKey: workflowKeys.bySlug(slug!),
        queryFn: async () => {
            const response = await coreApi.getPublicRapidWorkflowBySlug(slug!);
            return response.data || response;
        },
        enabled: !!slug && guestAuthInitialized,
        staleTime: Infinity
    });
}

export function useWorkflowById(id: string | undefined) {
    return useQuery<RapidWorkflowDataType>({
        queryKey: workflowKeys.byId(id!),
        queryFn: async () => {
            const snapshot = await getDoc(doc(db, "workflows", id!));
            return { ...snapshot.data(), id: snapshot.id } as RapidWorkflowDataType;
        },
        enabled: !!id,
        staleTime: Infinity
    });
}

export function useCreateWorkflow() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (props: CreateRapidWorkflowProps) => {
            const { groundTruthData, workspaceId, task, isSync, exampleData, buildMetadata } =
                props;

            if (!workspaceId) {
                throw new Error("No current workspace");
            }

            const workflow: RapidWorkflowDataType = await coreApi.createRapidWorkflow({
                groundTruthData,
                workspaceId,
                task,
                isSync,
                exampleData,
                buildMetadata
            });

            if (!workflow?.url) {
                throw new Error("Failed to create workflow -- no URL");
            }

            return workflow;
        },
        onSuccess: (workflow, variables) => {
            if (workflow.url && variables.workspaceId) {
                queryClient.setQueryData(
                    workflowKeys.byUrl(workflow.url, variables.workspaceId),
                    workflow
                );
            }
            if (workflow.id) {
                queryClient.setQueryData(workflowKeys.byId(workflow.id), workflow);
            }
        }
    });
}

export function useClearWorkflowQueries() {
    const queryClient = useQueryClient();
    return useCallback(
        () => queryClient.removeQueries({ queryKey: workflowKeys.all }),
        [queryClient]
    );
}
